import React, { useState } from 'react';
import { X, Upload, Camera, Mic, BookOpen } from 'lucide-react';
import API_URL from '../config/API_URL';

export const PersonalBookModal = ({ isOpen, onClose, onProceedToRecord }) => {
  const [bookTitle, setBookTitle] = useState('');
  const [bookAuthor, setBookAuthor] = useState('');
  const [coverImage, setCoverImage] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  const handleCoverUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCoverImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreateAndRecord = async () => {
    if (!bookTitle) {
      alert('Please enter a book title');
      return;
    }

    setIsCreating(true);
    
    try {
      const formData = new FormData();
      formData.append('title', bookTitle);
      formData.append('author', bookAuthor || 'Unknown');
      if (coverImage) {
        formData.append('cover_image', coverImage);
      }

      const response = await fetch(`${API_URL}/api/personal-books/create`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        onProceedToRecord(data.book);
        onClose();
      }
    } catch (error) {
      console.error('Error creating personal book:', error);
      alert('Failed to create book. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 50,
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '1rem',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        padding: '2rem'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
            Record Your Personal Book
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <X size={24} />
          </button>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
            Book Title *
          </label>
          <input
            type="text"
            value={bookTitle}
            onChange={(e) => setBookTitle(e.target.value)}
            placeholder="Enter the book title"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #e5e7eb',
              borderRadius: '0.5rem',
              fontSize: '1rem'
            }}
          />
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
            Author
          </label>
          <input
            type="text"
            value={bookAuthor}
            onChange={(e) => setBookAuthor(e.target.value)}
            placeholder="Enter the author's name"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #e5e7eb',
              borderRadius: '0.5rem',
              fontSize: '1rem'
            }}
          />
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
            Book Cover (Optional)
          </label>
          <div style={{
            border: '2px dashed #e5e7eb',
            borderRadius: '0.5rem',
            padding: '2rem',
            textAlign: 'center',
            background: coverPreview ? 'white' : '#f9fafb'
          }}>
            {coverPreview ? (
              <div>
                <img 
                  src={coverPreview} 
                  alt="Cover preview"
                  style={{
                    maxWidth: '200px',
                    maxHeight: '300px',
                    margin: '0 auto',
                    borderRadius: '0.5rem'
                  }}
                />
                <button
                  onClick={() => {
                    setCoverImage(null);
                    setCoverPreview(null);
                  }}
                  style={{
                    marginTop: '1rem',
                    padding: '0.5rem 1rem',
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: 'pointer'
                  }}
                >
                  Remove Cover
                </button>
              </div>
            ) : (
              <>
                <Upload size={32} color="#9ca3af" style={{ margin: '0 auto 0.5rem' }} />
                <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
                  Upload a photo of your book cover
                </p>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleCoverUpload}
                  style={{ display: 'none' }}
                  id="cover-upload"
                />
                <label
                  htmlFor="cover-upload"
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#9333ea',
                    color: 'white',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    display: 'inline-block'
                  }}
                >
                  Choose Photo
                </label>
              </>
            )}
          </div>
        </div>

        <div style={{
          background: '#f0fdf4',
          border: '1px solid #86efac',
          borderRadius: '0.5rem',
          padding: '1rem',
          marginBottom: '1.5rem'
        }}>
          <h4 style={{ color: '#166534', marginBottom: '0.5rem', fontWeight: '600' }}>
            Recording Options
          </h4>
          <p style={{ color: '#15803d', fontSize: '0.875rem' }}>
            After setting up your book, you'll be able to:
          </p>
          <ul style={{ marginTop: '0.5rem', marginLeft: '1.5rem', fontSize: '0.875rem', color: '#15803d' }}>
            <li>Record video of yourself reading the book</li>
            <li>Record audio-only narration</li>
            <li>Assign the recording to specific children or keep it unassigned</li>
          </ul>
        </div>

        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#e5e7eb',
              color: '#374151',
              border: 'none',
              borderRadius: '0.5rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleCreateAndRecord}
            disabled={!bookTitle || isCreating}
            style={{
              padding: '0.75rem 1.5rem',
              background: bookTitle && !isCreating ? '#10b981' : '#e5e7eb',
              color: bookTitle && !isCreating ? 'white' : '#9ca3af',
              border: 'none',
              borderRadius: '0.5rem',
              fontWeight: '600',
              cursor: bookTitle && !isCreating ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Camera size={20} />
            {isCreating ? 'Creating...' : 'Proceed to Record'}
          </button>
        </div>
      </div>
    </div>
  );
};